import {
  CreateQuestionSectionSchema,
  type CreateQuestionSectionRequest,
  type QuestionFlow,
} from '@aysar/contracts';
import { useTranslation } from 'react-i18next';

import { DynamicFormModal } from '@/shared/components/dynamic-form/DynamicFormModal/DynamicFormModal';
import { type ModalComponentProps } from '@/shared/components/Modals/modal-registry/modal-registry.types';
import { useToast } from '@/shared/hooks/useToast/useToast';
import { toApiError } from '@/shared/utils/transform-error/transform-error';

import { buildSectionFields } from '../../config/section-form.config';
import {
  useCreateSectionMutation,
  useSectionDetailQuery,
  useUpdateSectionMutation,
} from '../../services/question-sections/question-sections.api';

/**
 * Create or edit a section.
 *
 * Validated against `CreateQuestionSectionSchema` in **both** modes, because the
 * update contract is the create contract minus the immutable fields — and the form
 * still renders those, disabled, so an edit never looks like a different form.
 */
export function SectionFormModal({ open, onCancel, modalData }: ModalComponentProps) {
  const { t } = useTranslation('questions');
  const toast = useToast();

  const sectionId = typeof modalData?.['sectionId'] === 'string' ? modalData['sectionId'] : null;
  const flow =
    typeof modalData?.['flow'] === 'string' ? (modalData['flow'] as QuestionFlow) : 'registration';
  const isEditing = sectionId !== null;

  const detail = useSectionDetailQuery(sectionId ?? '', { skip: sectionId === null });
  const [createSection] = useCreateSectionMutation();
  const [updateSection] = useUpdateSectionMutation();

  return (
    <DynamicFormModal<CreateQuestionSectionRequest>
      isOpen={open}
      onOpenChange={(isOpen) => {
        if (!isOpen) onCancel();
      }}
      title={
        isEditing
          ? t('sections.form.editTitle', { title: detail.data?.title ?? '' })
          : t('sections.form.createTitle')
      }
      size="lg"
      // Held until the record arrives: RHF reads `defaultValues` once, so a form
      // mounted early opens blank and saving would wipe the section.
      isLoading={isEditing && detail.isLoading}
      fields={buildSectionFields(t, isEditing)}
      schema={CreateQuestionSectionSchema}
      initialValues={
        isEditing
          ? {
              key: detail.data?.key,
              flow: detail.data?.flow,
              isActive: detail.data?.isActive,
              translations: detail.data?.translations,
            }
          : { flow, isActive: true }
      }
      onCancel={onCancel}
      onSubmit={async (values) => {
        try {
          if (isEditing) {
            // `key` and `flow` are absent from the update contract by construction.
            await updateSection({
              id: sectionId,
              body: {
                isActive: values.isActive,
                translations: values.translations,
              },
            }).unwrap();
            toast.success(t('sections.form.updated'));
          } else {
            await createSection(values).unwrap();
            toast.success(t('sections.form.created'));
          }
          onCancel();
        } catch (error) {
          throw toApiError(error);
        }
      }}
    />
  );
}
